import { useState } from "react";
import { Modal } from "./Modal";
import { api, ApiError } from "../lib/api";

interface Props {
    title: string;
    /** What will happen, in plain words. Shown above any warning. */
    message: string;
    /**
     * The consequence the user might not expect — retention, audit visibility,
     * knock-on effects. Rendered in the warning style.
     */
    warning?: string;
    confirmLabel: string;
    /** "danger" reddens the confirm button. Use for anything irreversible. */
    tone?: "danger" | "normal";
    /** When set, a written reason is required and posted as { reason }. */
    requireReason?: boolean;
    reasonLabel?: string;
    minReason?: number;
    /** Endpoint to call. */
    request: { method: "post" | "delete"; path: string; body?: object };
    onClose: () => void;
    onDone: () => void;
}

export const ConfirmDialog = ({
    title, message, warning, confirmLabel, tone = "normal",
    requireReason, reasonLabel = "Reason", minReason = 10,
    request, onClose, onDone,
}: Props) => {
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const tooShort = requireReason && reason.trim().length < minReason;

    const go = async () => {
        setBusy(true);
        setError(null);
        try {
            if (request.method === "delete") {
                await api.delete(request.path);
            } else {
                await api.post(request.path, {
                    ...request.body,
                    ...(requireReason ? { reason: reason.trim() } : {}),
                });
            }
            onDone();
        } catch (err) {
            // Conflicts carry a specific explanation from the server — "this
            // incident cannot be closed until the Commission is notified" — and
            // that is far more useful than anything we could invent here.
            setError(err instanceof ApiError || err instanceof Error ? err.message : "That did not work");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal
            title={title}
            onClose={onClose}
            footer={
                <>
                    <button
                        className={`btn ${tone === "danger" ? "danger" : ""}`}
                        onClick={go}
                        disabled={busy || tooShort}
                    >
                        {busy ? "Working…" : confirmLabel}
                    </button>
                    <button className="btn ghost" onClick={onClose} disabled={busy}>
                        Cancel
                    </button>
                </>
            }
        >
            {error && <div className="error-banner">{error}</div>}
            <p className="confirm-message">{message}</p>
            {warning && <div className="warn-banner">{warning}</div>}

            {requireReason && (
                <div className="field">
                    <label htmlFor="confirm-reason">{reasonLabel}</label>
                    <textarea
                        id="confirm-reason"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="This is recorded against the record and in the audit log."
                    />
                    <span className="help" style={{ color: tooShort ? "var(--block)" : "var(--muted)" }}>
                        {reason.trim().length}/{minReason} characters
                    </span>
                </div>
            )}
        </Modal>
    );
};
